import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Users, Plus, UserCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function FamilyUsers() {
  const { householdId } = useParams();
  const navigate = useNavigate();
  const [household, setHousehold] = useState(null);
  const [familyUsers, setFamilyUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role: 'member'
  });

  useEffect(() => {
    fetchData();
  }, [householdId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const householdRes = await axios.get(`${API}/households/${householdId}`);
      setHousehold(householdRes.data);
      
      // Fetch family users from new endpoint
      try {
        const familyRes = await axios.get(`${API}/households/${householdId}/family`);
        setFamilyUsers(familyRes.data);
      } catch (error) {
        console.error('Error fetching family users:', error);
        setFamilyUsers([]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/family/register`, {
        household_id: householdId,
        ...newUser
      });
      toast.success('Familienmitglied erfolgreich hinzugefügt!');
      setShowAddForm(false);
      setNewUser({ name: '', email: '', password: '', role: 'member' });
      fetchData();
    } catch (error) {
      console.error('Error adding user:', error);
      toast.error('Fehler beim Hinzufügen des Familienmitglieds');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-6">
      <div className="max-w-7xl mx-auto">
        <Button
          variant="outline"
          onClick={() => navigate(`/households/${householdId}`)}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zurück
        </Button>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-6 h-6" />
              Familienmitglieder - {household?.name}
            </CardTitle>
            <CardDescription>
              Verwalten Sie Familienmitglieder mit Zugriff auf diesen Haushalt
            </CardDescription>
          </CardHeader>
        </Card>

        {!showAddForm && (
          <Button onClick={() => setShowAddForm(true)} className="mb-6">
            <Plus className="w-4 h-4 mr-2" />
            Familienmitglied hinzufügen
          </Button>
        )}

        {showAddForm && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Neues Familienmitglied hinzufügen</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAddUser} className="space-y-4">
                <div>
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={newUser.name}
                    onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="email">E-Mail *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="password">Passwort *</Label>
                  <Input
                    id="password"
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="role">Rolle</Label>
                  <Select
                    value={newUser.role}
                    onValueChange={(value) => setNewUser({ ...newUser, role: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Administrator</SelectItem>
                      <SelectItem value="member">Mitglied</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-2">
                  <Button type="submit">Hinzufügen</Button>
                  <Button type="button" variant="outline" onClick={() => setShowAddForm(false)}>
                    Abbrechen
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Family Users Liste */}
        {familyUsers.length === 0 ? (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center text-gray-500">
                <UserCircle className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                <p>Noch keine Familienmitglieder</p>
                <p className="text-sm mt-2">Fügen Sie Familienmitglieder über den Button oben hinzu</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {familyUsers.map((user) => (
              <Card key={user.id}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <UserCircle className="w-5 h-5" />
                      {user.name}
                    </div>
                    <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
                      {user.role === 'admin' ? 'Admin' : 'Mitglied'}
                    </Badge>
                  </CardTitle>
                  <CardDescription>{user.email}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-sm text-gray-600">
                    <p><strong>ID:</strong> {user.id.substring(0, 8)}...</p>
                    {user.created_at && (
                      <p className="mt-1">
                        <strong>Erstellt:</strong> {new Date(user.created_at).toLocaleDateString('de-DE')}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
